import { useSearchParams } from 'react-router-dom'

type ParamKey = string
type ParamValue = string

export const useQueryManager = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  // Get all values for a param key (e.g., all categories)
  const getValues = (key: ParamKey): ParamValue[] => {
    return searchParams.getAll(key)
  }

  // Set a single-value param (like "search=shoes")
  const setValue = (key: ParamKey, value: ParamValue) => {
    const newParams = new URLSearchParams(searchParams.toString())
    newParams.set(key, value)
    setSearchParams(newParams)
  }

  // Add a value to a multi-value param (e.g., add "Tech" to category)
  const addValue = (key: ParamKey, value: ParamValue) => {
    const current = new Set(getValues(key))
    current.add(value)

    const newParams = new URLSearchParams(searchParams.toString())
    newParams.delete(key)
    current.forEach((v) => newParams.append(key, v))
    setSearchParams(newParams)
  }

  // Remove a value from a multi-value param
  const removeValue = (key: ParamKey, value: ParamValue) => {
    const remaining = getValues(key).filter((v) => v !== value)

    const newParams = new URLSearchParams(searchParams.toString())
    newParams.delete(key)
    remaining.forEach((v) => newParams.append(key, v))
    setSearchParams(newParams)
  }

  // Toggle a value in a multi-value param
  const toggleValue = (key: ParamKey, value: ParamValue) => {
    if (getValues(key).includes(value)) {
      removeValue(key, value)
    } else {
      addValue(key, value)
    }
  }

  // Clear all values of a given key
  const clearKey = (key: ParamKey) => {
    const newParams = new URLSearchParams(searchParams.toString())
    newParams.delete(key)
    setSearchParams(newParams)
  }

  // Clear all query parameters
const clearAll = () => {
  setSearchParams(new URLSearchParams());
}

  return {
    getValues,
    setValue,
    addValue,
    removeValue,
    toggleValue,
    clearAll,
    clearKey,
    
    allParams: Object.fromEntries(searchParams.entries()),
  }
}
